import React, { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Field, Pick, CustomFieldInput } from "@/components/inquiries/InquiryFormDialog";
import { ContactPicker } from "@/components/inquiries/ContactPicker";
import { PRIORITY_OPTIONS } from "@/lib/inquiry";

const KEYS = ["direction", "inquiry_date", "subject", "purpose", "description", "category", "channel", "priority",
  "entity", "organisation", "contact_ids", "primary_contact_id", "assigned_to", "custom"];

export const EditInquiryDialog = ({ open, inquiry, settings, onClose, onSaved }) => {
  const [f, setF] = useState({});
  const [people, setPeople] = useState([]);
  const [entities, setEntities] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    setF(Object.fromEntries(KEYS.map((k) => [k, inquiry[k] ?? (k === "custom" ? {} : k === "contact_ids" ? [] : "")])));
    api.get("/users/assignable?team=1").then((r) => setPeople(r.data)).catch(() => {});
    api.get("/entities").then((r) => setEntities(r.data.map((e) => e.name))).catch(() => {});
  }, [open, inquiry]);
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v?.target ? v.target.value : v }));
  const save = async () => {
    setBusy(true);
    try {
      const body = Object.fromEntries(Object.entries(f).filter(([k, v]) => JSON.stringify(v ?? "") !== JSON.stringify(inquiry[k] ?? (k === "custom" ? {} : k === "contact_ids" ? [] : ""))));
      if (!Object.keys(body).length) { onClose(); return; }
      const { data } = await api.patch(`/inquiries/${inquiry.id}`, body);
      toast.success("Details updated — the change is logged in the journey"); window.dispatchEvent(new Event("inquiries:changed")); onSaved(data);
    } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };
  if (!open) return null;
  const people2 = people.some((p) => p.id === inquiry.assigned_to) ? people : [...people, { id: inquiry.assigned_to, name: inquiry.assigned_name }];
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="edit-inquiry-dialog">
        <DialogHeader><DialogTitle>Edit {inquiry.number}</DialogTitle><DialogDescription>Old and new values are written to the journey.</DialogDescription></DialogHeader>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <Field label="Type"><Pick testid="edit-direction" value={f.direction} onChange={set("direction")} options={[{ value: "inbound", label: "Inbound" }, { value: "outbound", label: "Outbound" }]} /></Field>
          <Field label="Inquiry date"><Input data-testid="edit-date" type="date" value={f.inquiry_date || ""} onChange={set("inquiry_date")} /></Field>
          <Field label="Priority"><Pick testid="edit-priority" value={f.priority} onChange={set("priority")} options={PRIORITY_OPTIONS} /></Field>
          <Field label="Subject" className="col-span-2 md:col-span-3"><Input data-testid="edit-subject" value={f.subject || ""} onChange={set("subject")} /></Field>
          <Field label="Purpose" className="col-span-2 md:col-span-3"><Input data-testid="edit-purpose" value={f.purpose || ""} onChange={set("purpose")} /></Field>
          <Field label="Category"><Pick testid="edit-category" value={f.category} onChange={set("category")} options={settings.categories} /></Field>
          <Field label="Channel"><Pick testid="edit-channel" value={f.channel} onChange={set("channel")} options={settings.channels} /></Field>
          <Field label="Assigned to"><Pick testid="edit-assignee" value={f.assigned_to} onChange={set("assigned_to")} options={people2.map((p) => ({ value: p.id, label: p.name }))} /></Field>
          <Field label="Party organisation"><Input data-testid="edit-organisation" value={f.organisation || ""} onChange={set("organisation")} /></Field>
          <Field label="Our entity"><Pick testid="edit-entity" value={f.entity} onChange={set("entity")} options={[...new Set([...entities, ...(f.entity ? [f.entity] : [])])]} placeholder="Optional" /></Field>
          <Field label="Contacts" className="col-span-2 md:col-span-3"><ContactPicker value={f.contact_ids || []} onChange={set("contact_ids")} primary={f.primary_contact_id} onPrimary={set("primary_contact_id")} /></Field>
          <Field label="Description" className="col-span-2 md:col-span-3"><Textarea data-testid="edit-description" rows={3} value={f.description || ""} onChange={set("description")} /></Field>
          {settings.custom_fields.map((cf) => (
            <Field key={cf.key} label={cf.label}><CustomFieldInput f={cf} value={f.custom?.[cf.key]} onChange={(v) => setF((x) => ({ ...x, custom: { ...x.custom, [cf.key]: v } }))} /></Field>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="edit-save" disabled={busy} onClick={save} className="bg-blue-700 hover:bg-blue-800 text-white">Save changes</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
